// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — the board, on a canvas, from your own end of it.
//
// EVERY PLAYER SEES THEIR OWN FORGE AT THE BOTTOM. The map is 180°-rotationally symmetric, so
// showing seat 1 the board from its own end is exactly a rotation — one boolean in the projection,
// no second map, and no chance of the two peers looking at different geometry. It is also the
// strongest side cue the game has: mine is the one at the bottom.
//
// THREE CHANNELS CARRY OWNERSHIP, so colour is never load-bearing on its own: hue (cyan/amber, a
// blue-orange axis rather than red-green), fill style (yours solid with a light rim, theirs hollow
// with a heavy rim), and glyph orientation (your silhouettes point up-field, theirs point down). In
// pure greyscale the board is still playable.
//
// HIT POINTS ARE THE GLYPH'S FILL HEIGHT rather than a bar above it. A unit at 40% is drawn with
// only its bottom 40% filled and the rest as outline. That removes twenty-four little bars from a
// 360px-wide board, and it is the number a player checks constantly.

import { COLS, HALF, MIRROR_X, MIRROR_Y, ROWS, SUB, Terrain, colOf, idx, rowOf } from './map';
import { GLYPH, GLYPH_SCALE, PALETTE, SEAT_COLOUR } from './palette';
import { Stance, opacity, tileOf, tonnage, type State } from './sim';
import { UNITS } from './units';

export interface View {
  /** Device-pixel canvas size and the CSS size it is presented at. */
  tile: number;
  ox: number;
  oy: number;
  w: number;
  h: number;
  /** True when the local player is seat 1 and the board is rotated. */
  flip: boolean;
}

export interface Paint {
  view: View;
  state: State;
  seat: number;
  selection: ReadonlySet<number>;
  /** Tiles the current lasso covers, in screen space. */
  lasso: { x0: number; y0: number; x1: number; y1: number } | null;
  /** Recent shots, for the tracer layer: [fromX, fromY, toX, toY, age, seat]. */
  tracers: number[][];
  hint: number;
  reducedMotion: boolean;
}

/**
 * The board's pixel geometry. The tile size is CLAMPED and derived from the space actually
 * available, never hard-coded: a 320px phone with a browser toolbar has about 460 usable pixels and
 * a desktop window has nine hundred, and the same board has to be legible in both. The floor of 18
 * is the point below which a unit glyph stops being identifiable; below that the board scrolls
 * rather than shrinking further, which is the honest failure.
 */
export function layout(availW: number, availH: number): { tile: number; w: number; h: number } {
  const t = Math.max(18, Math.min(44, Math.floor(Math.min((availW - 8) / COLS, availH / ROWS))));
  return { tile: t, w: t * COLS, h: t * ROWS };
}

/**
 * The board's size, clamped against the VIEWPORT as well as its host.
 *
 * The canvas is absolutely positioned (see the note in main.css), which means an oversized board
 * hangs off both edges of the screen WITHOUT extending `document.documentElement.scrollWidth` — so
 * the standard "is there horizontal overflow" check reads clean while the two outermost columns of
 * the map are off-screen and untappable. Measured: after a viewport change the ResizeObserver had
 * not yet fired, the board was still sized for a desktop window, and it rendered 420px wide inside
 * 375px with its left edge at −22. Taking the viewport into account makes that unrepresentable
 * rather than merely unlikely.
 */
export function boardSize(hostW: number, hostH: number, viewW: number, viewH: number): { tile: number; w: number; h: number } {
  return layout(Math.min(hostW > 20 ? hostW : viewW, viewW), Math.min(hostH > 20 ? hostH : viewH, viewH));
}

/** Sub-unit world position to canvas pixels, rotating the whole board for seat 1. */
export function project(v: View, x: number, y: number): [number, number] {
  const wx = v.flip ? MIRROR_X - x : x;
  const wy = v.flip ? MIRROR_Y - y : y;
  return [v.ox + (wx * v.tile) / SUB, v.oy + (wy * v.tile) / SUB];
}

/** Canvas pixels back to a tile — the inverse the input layer needs. */
export function unproject(v: View, px: number, py: number): number {
  let c = Math.floor((px - v.ox) / v.tile);
  let r = Math.floor((py - v.oy) / v.tile);
  if (v.flip) {
    c = COLS - 1 - c;
    r = ROWS - 1 - r;
  }
  if (c < 0 || c >= COLS || r < 0 || r >= ROWS) return -1;
  return idx(c, r);
}

/** The screen rectangle of a tile, for the input layer's hit tests and the tutorial's spotlight. */
export function tileRect(v: View, t: number): [number, number, number, number] {
  const c = v.flip ? COLS - 1 - colOf(t) : colOf(t);
  const r = v.flip ? ROWS - 1 - rowOf(t) : rowOf(t);
  return [v.ox + c * v.tile, v.oy + r * v.tile, v.tile, v.tile];
}

function drawTerrain(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  g.fillStyle = PALETTE.ground;
  g.fillRect(0, 0, v.w, v.h);
  for (let t = 0; t < COLS * ROWS; t++) {
    const [x, y, w, h] = tileRect(v, t);
    const kind = s.map.terrain[t];
    if (kind === Terrain.Ridge) g.fillStyle = PALETTE.ridge;
    else if (kind === Terrain.Scrub) g.fillStyle = PALETTE.scrub;
    else if (kind === Terrain.Forge) g.fillStyle = PALETTE.open;
    else g.fillStyle = PALETTE.open;
    g.fillRect(x, y, w, h);
    if (kind === Terrain.Scrub) {
      // The hatch is what carries "you cannot see through this", and it is the mark the contrast
      // test holds to 3:1 — the fill behind it is deliberately dark and deliberately not the claim.
      // CLIP FIRST, THEN BUILD THE PATH: beginPath() after clip() would discard the hatch and leave
      // the stroke drawing the clip rectangle instead, which is a scrub tile with no hatch on it at
      // all — drawn, correct, and invisible.
      g.save();
      g.beginPath();
      g.rect(x, y, w, h);
      g.clip();
      g.strokeStyle = PALETTE.scrubhatch;
      g.lineWidth = Math.max(1.5, v.tile / 10);
      g.beginPath();
      for (let k = -h; k < w + h; k += Math.max(5, v.tile / 2.6)) {
        g.moveTo(x + k, y + h);
        g.lineTo(x + k - h, y);
      }
      g.stroke();
      g.restore();
    }
    g.strokeStyle = PALETTE.ground;
    g.lineWidth = 1;
    g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  }

  // The Forges, drawn as one 2x2 block each with a health bar along their own edge.
  for (let seat = 0; seat < 2; seat++) {
    const tiles = s.map.forgeTiles[seat];
    let minX = 1e9;
    let minY = 1e9;
    let maxX = -1e9;
    let maxY = -1e9;
    for (const t of tiles) {
      const [x, y, w, h] = tileRect(v, t);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
    const col = SEAT_COLOUR[seat];
    g.fillStyle = PALETTE.open;
    g.fillRect(minX, minY, maxX - minX, maxY - minY);
    g.strokeStyle = col;
    g.lineWidth = Math.max(2, v.tile / 8);
    g.strokeRect(minX + g.lineWidth / 2, minY + g.lineWidth / 2, maxX - minX - g.lineWidth, maxY - minY - g.lineWidth);
    const frac = Math.max(0, s.sides[seat].forgeHp) / 1000;
    g.fillStyle = col;
    g.globalAlpha = 0.45;
    g.fillRect(minX, maxY - (maxY - minY) * frac, maxX - minX, (maxY - minY) * frac);
    g.globalAlpha = 1;
  }
}

function drawSeams(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  for (const seam of s.seams) {
    const tiles = seam.foundry ? s.map.foundryTiles : [seam.tile];
    let minX = 1e9;
    let minY = 1e9;
    let maxX = -1e9;
    let maxY = -1e9;
    for (const t of tiles) {
      const [x, y, w, h] = tileRect(v, t);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
    const owned = seam.owner !== 2;
    const col = owned ? SEAT_COLOUR[seam.owner] : PALETTE.seam;
    const pad = Math.max(2, v.tile / 8);
    g.lineWidth = Math.max(2, v.tile / 10);
    g.strokeStyle = col;
    if (seam.contested) g.setLineDash([Math.max(3, v.tile / 5), Math.max(3, v.tile / 5)]);
    if (owned) {
      // Nearly opaque, because the numeral inside is drawn in `ink` and the contrast test asserts
      // ink-on-seat-colour. A 35% wash would leave the test measuring a pairing the renderer never
      // actually paints — green on a claim nobody can read.
      g.fillStyle = col;
      g.globalAlpha = 0.9;
      g.fillRect(minX + pad, minY + pad, maxX - minX - pad * 2, maxY - minY - pad * 2);
      g.globalAlpha = 1;
    }
    g.strokeRect(minX + pad, minY + pad, maxX - minX - pad * 2, maxY - minY - pad * 2);
    g.setLineDash([]);

    // Capture progress as a bar along the bottom edge, so "two seconds of standing here" is a thing
    // you watch fill rather than a rule you are told.
    if (seam.cap > 0 && seam.cap < 40) {
      g.fillStyle = SEAT_COLOUR[seam.capSide] ?? PALETTE.seam;
      g.fillRect(minX + pad, maxY - pad - 3, ((maxX - minX - pad * 2) * seam.cap) / 40, 3);
    }
    if (v.tile >= 22) {
      g.fillStyle = owned ? PALETTE.ink : PALETTE.seam;
      g.font = `600 ${Math.round(v.tile * 0.34)}px system-ui, -apple-system, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(String(seam.ore), (minX + maxX) / 2, (minY + maxY) / 2);
    }
  }
}

function drawWrecks(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  for (const w of s.wrecks) {
    const [x, y, tw, th] = tileRect(v, w.tile);
    const cx = x + tw / 2;
    const cy = y + th / 2;
    const r = tw * 0.3;
    g.strokeStyle = PALETTE.wreck;
    g.lineWidth = Math.max(2, v.tile / 8);
    g.beginPath();
    g.moveTo(cx - r, cy - r);
    g.lineTo(cx + r, cy + r);
    g.moveTo(cx + r, cy - r);
    g.lineTo(cx - r, cy + r);
    g.stroke();
    if (v.tile >= 24) {
      g.fillStyle = PALETTE.wreck;
      g.font = `600 ${Math.round(v.tile * 0.28)}px system-ui, -apple-system, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'top';
      g.fillText(String(w.ore), cx, y + 1);
    }
  }
}

function drawUnit(g: CanvasRenderingContext2D, p: Paint, e: number): void {
  const { view: v, state: s } = p;
  const spec = UNITS[s.type[e]];
  const [cx, cy] = project(v, s.x[e], s.y[e]);
  const mine = s.seat[e] === p.seat;
  const col = SEAT_COLOUR[s.seat[e]];
  const size = v.tile * (GLYPH_SCALE[spec.id] ?? 0.6);
  const poly = GLYPH[spec.id];
  // Your silhouettes point up-field and theirs point down — a third ownership channel that survives
  // greyscale and a colour-blind palette alike.
  const dir = mine ? -1 : 1;

  const path = new Path2D();
  poly.forEach(([px, py], i) => {
    const X = cx + px * size * (dir === -1 ? 1 : -1);
    const Y = cy + py * size * dir * -1;
    if (i === 0) path.moveTo(X, Y);
    else path.lineTo(X, Y);
  });
  path.closePath();

  // Hit points as FILL HEIGHT. Clip the glyph and fill only the bottom fraction of it.
  const frac = Math.max(0, Math.min(1, s.hp[e] / spec.hp));
  g.save();
  g.clip(path);
  g.fillStyle = mine ? col : PALETTE.ground;
  g.globalAlpha = mine ? 1 : 0.55;
  g.fillRect(cx - size, cy - size, size * 2, size * 2);
  g.globalAlpha = 1;
  g.fillStyle = col;
  g.fillRect(cx - size, cy + size - size * 2 * frac, size * 2, size * 2 * frac);
  g.restore();

  g.strokeStyle = col;
  g.lineWidth = mine ? Math.max(1.5, v.tile / 16) : Math.max(2.5, v.tile / 10);
  g.stroke(path);

  // Armour as notches along the top edge — losing a step is something you SEE happen.
  if (s.armour[e] > 0 && v.tile >= 20) {
    g.fillStyle = mine ? PALETTE.ink : col;
    const n = s.armour[e];
    const nw = Math.max(2, v.tile / 12);
    for (let i = 0; i < n; i++) {
      g.fillRect(cx - (n * (nw + 2)) / 2 + i * (nw + 2), cy - size * 0.62, nw, Math.max(2, v.tile / 14));
    }
  }
  if (s.stance[e] === Stance.Hold && mine) {
    g.fillStyle = PALETTE.ring;
    g.fillRect(cx - size * 0.5, cy + size * 0.62, size, Math.max(2, v.tile / 16));
  }
  if (p.selection.has(e)) {
    g.strokeStyle = PALETTE.ring;
    g.lineWidth = Math.max(1.5, v.tile / 18);
    g.beginPath();
    g.arc(cx, cy, size * 0.95, 0, Math.PI * 2);
    g.stroke();
  }
}

function drawOrders(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  if (p.selection.size === 0) return;
  g.strokeStyle = PALETTE.order;
  g.lineWidth = Math.max(1, v.tile / 22);
  g.setLineDash([Math.max(2, v.tile / 8), Math.max(3, v.tile / 6)]);
  for (const e of p.selection) {
    if (!s.alive[e]) continue;
    const goal = s.dest[e] >= 0 ? s.dest[e] : s.foe[e] >= 0 && s.alive[s.foe[e]] ? tileOf(s, s.foe[e]) : -1;
    if (goal < 0) continue;
    const [x0, y0] = project(v, s.x[e], s.y[e]);
    const [gx, gy, gw, gh] = tileRect(v, goal);
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(gx + gw / 2, gy + gh / 2);
    g.stroke();
  }
  g.setLineDash([]);
}

/**
 * The blocked-fire mark: a hollow chevron over any of YOUR ranged units that has an enemy in range
 * and cannot shoot it. Without this the mechanic is negative space — the feedback for "your Arbalest
 * is blocked" is that nothing happens — and a rule whose only symptom is silence reads as a bug.
 */
function drawBlocked(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  const op = opacity(s);
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || s.seat[e] !== p.seat) continue;
    const spec = UNITS[s.type[e]];
    if (spec.reach < 20) continue;
    let inRange = false;
    let visible = false;
    for (let t = 0; t < s.nextId; t++) {
      if (!s.alive[t] || s.seat[t] === p.seat) continue;
      const dx = s.x[e] - s.x[t];
      const dy = s.y[e] - s.y[t];
      if (dx * dx + dy * dy > spec.range2) continue;
      inRange = true;
      const a = tileOf(s, e);
      const b = tileOf(s, t);
      let clear = true;
      // A cheap re-use of the sim's own opacity grid: if any tile strictly between is solid, the
      // shot is blocked. The exact rule is sim.ts's; this only decides whether to draw the mark.
      const steps = Math.max(Math.abs(colOf(a) - colOf(b)), Math.abs(rowOf(a) - rowOf(b)));
      for (let i = 1; i < steps; i++) {
        const c = colOf(a) + Math.round(((colOf(b) - colOf(a)) * i) / steps);
        const r = rowOf(a) + Math.round(((rowOf(b) - rowOf(a)) * i) / steps);
        if (op[idx(c, r)] === 1) clear = false;
      }
      if (clear) visible = true;
    }
    if (!inRange || visible) continue;
    const [cx, cy] = project(v, s.x[e], s.y[e]);
    const r = v.tile * 0.3;
    g.strokeStyle = PALETTE.ring;
    g.lineWidth = Math.max(1.5, v.tile / 16);
    g.beginPath();
    g.moveTo(cx - r, cy - v.tile * 0.55);
    g.lineTo(cx, cy - v.tile * 0.55 - r * 0.7);
    g.lineTo(cx + r, cy - v.tile * 0.55);
    g.stroke();
  }
}

export function paint(g: CanvasRenderingContext2D, p: Paint): void {
  const { view: v, state: s } = p;
  drawTerrain(g, p);
  drawSeams(g, p);
  drawWrecks(g, p);
  drawOrders(g, p);

  if (p.hint >= 0) {
    const [x, y, w, h] = tileRect(v, p.hint);
    g.strokeStyle = PALETTE.ring;
    g.lineWidth = Math.max(2, v.tile / 10);
    g.setLineDash([Math.max(3, v.tile / 5), Math.max(3, v.tile / 5)]);
    g.strokeRect(x + 2, y + 2, w - 4, h - 4);
    g.setLineDash([]);
  }

  for (let e = 0; e < s.nextId; e++) if (s.alive[e]) drawUnit(g, p, e);
  drawBlocked(g, p);

  if (!p.reducedMotion) {
    for (const t of p.tracers) {
      g.strokeStyle = SEAT_COLOUR[t[5]];
      g.globalAlpha = Math.max(0, 1 - t[4] / 6);
      g.lineWidth = Math.max(1, v.tile / 24);
      const [x0, y0] = project(v, t[0], t[1]);
      const [x1, y1] = project(v, t[2], t[3]);
      g.beginPath();
      g.moveTo(x0, y0);
      g.lineTo(x1, y1);
      g.stroke();
      g.globalAlpha = 1;
    }
  }

  if (p.lasso) {
    g.strokeStyle = PALETTE.ring;
    g.lineWidth = 1.5;
    g.setLineDash([4, 4]);
    g.strokeRect(
      Math.min(p.lasso.x0, p.lasso.x1),
      Math.min(p.lasso.y0, p.lasso.y1),
      Math.abs(p.lasso.x1 - p.lasso.x0),
      Math.abs(p.lasso.y1 - p.lasso.y0),
    );
    g.setLineDash([]);
  }

  // The rust vignette: no text, just the board's edge closing in and thickening at each doubling.
  if (s.tick >= s.mode.rustAt) {
    const steps = Math.min(6, Math.floor((s.tick - s.mode.rustAt) / 600) + 1);
    const band = Math.min(v.tile * 1.6, v.tile * 0.3 * steps);
    const grad = g.createLinearGradient(0, 0, 0, v.h);
    grad.addColorStop(0, PALETTE.rust);
    grad.addColorStop(band / v.h, 'rgba(0,0,0,0)');
    grad.addColorStop(1 - band / v.h, 'rgba(0,0,0,0)');
    grad.addColorStop(1, PALETTE.rust);
    g.globalAlpha = 0.35;
    g.fillStyle = grad;
    g.fillRect(0, 0, v.w, v.h);
    g.globalAlpha = 1;
  }
}

/** The split tonnage bar — the always-on answer to "who is winning", and why the number moved. */
export function tonnageSplit(s: State, seat: number): { mine: number; theirs: number; frac: number } {
  const mine = tonnage(s, seat);
  const theirs = tonnage(s, 1 - seat);
  const total = mine + theirs;
  return { mine, theirs, frac: total > 0 ? mine / total : 0.5 };
}

export { HALF, SUB };
